import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Tooltip,
  IconButton,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  Star,
  TrendingUp,
  Business,
  People,
  Store,
  Payment,
  History,
  Cancel,
  Refresh,
  CreditCard,
  Receipt,
  Warning,
  Info,
  Add,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import AlertMessageDialog from '../../../components/Dialogs/alertMessageDialog';
import { fetchSucursales } from '../../../utils/generalsFetch';

const SubscriptionTab = () => {
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [branchSubscriptions, setBranchSubscriptions] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alertDialog, setAlertDialog] = useState({ open: false, message: '', severity: 'success' });


 
  // Fetch branch subscriptions
  const fetchBranchSubscriptions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/subscriptions/branches?idcompany=${localStorage.getItem('idcompany')}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBranchSubscriptions(data.subscriptions || []);
      }
    } catch (error) {
      console.error('Error fetching branch subscriptions:', error);
    }
  };

  // Fetch usage statistics for selected branch
  const fetchUsageStats = async (branchId) => {
    if (!branchId) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/subscriptions/usage/${branchId}?idcompany=${localStorage.getItem('idcompany')}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsageStats(data.usage);
      } else {
        setUsageStats(null);
        console.error('Error response:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching usage stats:', error);
    }
  };

  // Fetch billing history
  const fetchBillingHistory = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/subscriptions/billing-history?idcompany=${localStorage.getItem('idcompany')}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBillingHistory(data.billing_history || []);
      }
    } catch (error) {
      console.error('Error fetching billing history:', error);
    }
  };


  // Cancel subscription
  const cancelSubscription = async (subscriptionId) => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/subscriptions/${subscriptionId}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });

      if (response.ok) {
        setAlertDialog({
          open: true,
          message: 'Suscripción cancelada exitosamente',
          severity: 'success'
        });
        fetchBranchSubscriptions();
        if (selectedBranch) {
          fetchUsageStats(selectedBranch.id);
        }
      } else {
        const errorData = await response.json();
        setAlertDialog({
          open: true,
          message: errorData.error || 'Error al cancelar suscripción',
          severity: 'error'
        });
      }
    } catch (error) {
      setAlertDialog({
        open: true,
        message: 'Error al cancelar suscripción',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSucursales({ idcompany: localStorage.getItem('idcompany'), usuarioId: localStorage.getItem('id') }).then((data) => {
      const transformedBranches = data.map(branch => ({
        id: branch.value,
        name: branch.label
      }));
      setBranches(transformedBranches);
      if (transformedBranches.length > 0 && !selectedBranch) {
        setSelectedBranch(transformedBranches[0]);
      }
    });
    fetchBranchSubscriptions();
    fetchBillingHistory();
  }, []);

  useEffect(() => {
    if (selectedBranch) {
      fetchUsageStats(selectedBranch.id);
    }
  }, [selectedBranch]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'trial': return 'info';
      case 'cancelled': return 'error';
      case 'expired': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle />;
      case 'trial': return <Info />;
      case 'cancelled': return <Cancel />;
      case 'expired': return <Warning />;
      default: return <Info />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-DO');
  };

  const getCurrentBranchSubscription = () => {
    if (!selectedBranch) return null;
    return branchSubscriptions.find(sub => sub.branch_id === selectedBranch.id);
  };

  const renderBranchSelector = () => {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom textAlign={"left"}>
            Seleccionar Sucursal
          </Typography>
          <FormControl fullWidth>
            <Select
              value={selectedBranch?.id || ''}
              onChange={(e) => {
                const branch = branches.find(b => b.id === e.target.value);
                setSelectedBranch(branch);
              }}
              displayEmpty
              sx={{ backgroundColor: 'white' }}
            >
              <MenuItem value="" disabled>
                Selecciona una sucursal
              </MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>
    );
  };

  const renderCurrentSubscription = () => {
    const currentSubscription = getCurrentBranchSubscription();
    
    if (!currentSubscription) {
      return (
        <Card sx={{ mb: 3, border: '2px dashed', borderColor: 'primary.main' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Business sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" fontWeight={600} gutterBottom textAlign={"center"}>
              Sin Suscripción Activa
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Esta sucursal no tiene una suscripción activa. Selecciona un plan para comenzar.
            </Typography>
            <Button
              color="primarybutton"
              variant="contained"
              size="large"
              startIcon={<Add />}
              onClick={() => navigate('/configuracion/subscription-plans')}
              sx={{ fontWeight: 'bold' }}
              disabled={!selectedBranch}
            >
              Seleccionar Plan
            </Button>
          </CardContent>
        </Card>
      );
    }
    
    return (
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #55b4ac 0%, #18586b 100%)' }}>
        <CardContent sx={{ color: 'white' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                {currentSubscription.plan_name}
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, ml: 1 }} textAlign={"left"}>
                {formatCurrency(currentSubscription.price)} / {currentSubscription.billing_cycle === 'monthly' ? 'mes' : 'año'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, mt: 1, ml: 1 }} textAlign={"left"}>
                Sucursal: {currentSubscription.branch_name}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Chip
                  icon={getStatusIcon(currentSubscription.status)}
                  label={currentSubscription.status === 'active' ? 'Activa' : 'En Prueba'}
                  color="primary"
                  variant="outlined"
                  sx={{ color: 'white', borderColor: 'white' }}
                />
                {currentSubscription.auto_renew && (
                  <Chip
                    label="Renovación Automática"
                    color="success"
                    variant="outlined"
                    sx={{ color: 'white', borderColor: 'white' }}
                  />
                )}
              </Stack>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Vence el
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {formatDate(currentSubscription.end_date)}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
        <CardActions sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.1)' }}>
          <Button
            variant="outlined"
            startIcon={<Payment />}
            sx={{ color: 'white', borderColor: 'white', fontWeight: 'bold' }}
            onClick={() => navigate('/configuracion/subscription-plans')}
          >
            Cambiar Plan
          </Button>
          {currentSubscription.status === 'active' && (
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              sx={{ color: 'white', borderColor: 'white', fontWeight: 'bold' }}
              onClick={() => cancelSubscription(currentSubscription.id)}
            >
              Cancelar
            </Button>
          )}
        </CardActions>
      </Card>
    );
  };

  const renderUsageStats = () => {
    if (!usageStats) return null;

    return (
      <Card sx={{ 
        mb: 3, 
        background: 'linear-gradient(135deg, rgba(85, 180, 172, 0.03) 0%, rgba(24, 88, 107, 0.03) 100%)',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 2px 8px rgba(85, 180, 172, 0.1)'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box sx={{ 
              width: 40, 
              height: 40, 
              borderRadius: '50%', 
              background: 'linear-gradient(135deg, #55b4ac 0%, #18586b 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2
            }}>
              <TrendingUp sx={{ color: 'white', fontSize: 20 }} />
            </Box>
            <Typography variant="h6" fontWeight={600} sx={{ color: '#18586b' }}>
              Uso Actual del Plan
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                background: 'rgba(255, 255, 255, 0.7)',
                border: '1px solid rgba(85, 180, 172, 0.2)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.9)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(85, 180, 172, 0.15)'
                }
              }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <People sx={{ color: '#55b4ac', mr: 1, fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      Usuarios
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={700} sx={{ color: '#18586b', ml: 1 }}>
                    {usageStats.users.current} / {usageStats.users.max === 999999 ? '∞' : usageStats.users.max}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={usageStats.users.percentage}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    bgcolor: alpha('#55b4ac', 0.1),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 5,
                      background: usageStats.users.percentage > 80 
                        ? 'linear-gradient(90deg, #f44336 0%, #d32f2f 100%)'
                        : 'linear-gradient(90deg, #55b4ac 0%, #18586b 100%)'
                    }
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {usageStats.users.percentage.toFixed(1)}% utilizado
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 2, 
                background: 'rgba(255, 255, 255, 0.7)',
                border: '1px solid rgba(85, 180, 172, 0.2)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.9)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(85, 180, 172, 0.15)'
                }
              }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CreditCard sx={{ color: '#55b4ac', mr: 1, fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      Préstamos Activos
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={700} sx={{ color: '#18586b', ml: 1 }}>
                    {usageStats.loans?.current || 0} / {usageStats.loans?.max === 999999 ? '∞' : usageStats.loans?.max || 0}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={usageStats.loans?.percentage || 0}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    bgcolor: alpha('#55b4ac', 0.1),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 5,
                      background: (usageStats.loans?.percentage || 0) > 80 
                        ? 'linear-gradient(90deg, #f44336 0%, #d32f2f 100%)'
                        : 'linear-gradient(90deg, #55b4ac 0%, #18586b 100%)'
                    }
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {(usageStats.loans?.percentage || 0).toFixed(1)}% utilizado
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };


  const renderBillingHistory = () => {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Historial de Facturación
          </Typography>
          {billingHistory.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Receipt sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                No hay historial de facturación disponible
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Sucursal</TableCell>
                    <TableCell>Monto</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Método de Pago</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {billingHistory.map((bill) => (
                    <TableRow key={bill.id}>
                      <TableCell>{formatDate(bill.billing_date)}</TableCell>
                      <TableCell>{bill.plan_name}</TableCell>
                      <TableCell>{bill.branch_name}</TableCell>
                      <TableCell>{formatCurrency(bill.amount)}</TableCell>
                      <TableCell>
                        <Chip
                          label={bill.status}
                          color={bill.status === 'paid' ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{bill.payment_method || 'N/A'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    );
  };


  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} color="text.primary">
            Gestión de Suscripciones por Sucursal
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Administra las suscripciones de cada sucursal y facturación
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => {
            fetchBranchSubscriptions();
            if (selectedBranch) {
              fetchUsageStats(selectedBranch.id);
            }
            fetchBillingHistory();
          }}
          sx={{ fontWeight: 'bold' }}
        >
          Actualizar
        </Button>
      </Box>

      {renderBranchSelector()}
      {renderCurrentSubscription()}
      {renderUsageStats()}
      {renderBillingHistory()}

      <AlertMessageDialog
        open={alertDialog.open}
        message={alertDialog.message}
        severity={alertDialog.severity}
        handleClose={() => setAlertDialog({ ...alertDialog, open: false })}
      />
    </Box>
  );
};

export default SubscriptionTab;
